import {
  BUILTIN_CATEGORIES,
  DEFAULT_CALENDARS,
  ICON_LABELS,
  SKATTEFIX_CALENDAR,
  Temporal,
  formatDateLong as formatDateLongSv,
  formatDateShort as formatDateShortSv,
  monthName as monthNameSv,
  tagExplanation as tagExplanationSv,
  weekdayName as weekdayNameSv,
  type CalendarDef,
  type Category,
  type DeadlineStatus,
  type IconId,
  type ISODate,
  type ISOInstant,
  type KnownTag,
  type TagInfo,
} from '@autoday/core';
import { currentLang, tr } from './i18n.ts';

/**
 * Datum, tider och systemgenererade etiketter på valt språk. Svenska använder exakt samma funktioner som
 * förut (core), så befintliga texter är oförändrade. Engelska skriver månader och veckodagar med namn
 * (inga sifferdatum som kan läsas på amerikanskt eller brittiskt sätt) och behåller 24-timmarsklocka och
 * måndag som första veckodag – språket ändrar aldrig tidszon eller klockformat.
 */
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EN_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const pad = (n: number) => String(n).padStart(2, '0');

export function monthName(m: number): string {
  return currentLang() === 'en' ? EN_MONTHS[m - 1] ?? '' : monthNameSv(m);
}
export function monthShort(m: number): string {
  if (currentLang() === 'en') return (EN_MONTHS[m - 1] ?? '').slice(0, 3);
  return ['jan', 'feb', 'mars', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'][m - 1] ?? '';
}
export function weekdayName(dow: number, short = false): string {
  if (currentLang() !== 'en') return weekdayNameSv(dow, short);
  const w = EN_WEEKDAYS[dow - 1] ?? '';
  return short ? w.slice(0, 3) : w;
}

/** sv: "måndag 12 oktober 2026" · en: "Monday 12 October 2026" */
export function formatDateLong(d: ISODate, withYear = true): string {
  if (currentLang() !== 'en') return formatDateLongSv(d, withYear);
  const p = Temporal.PlainDate.from(d);
  return `${weekdayName(p.dayOfWeek)} ${p.day} ${monthName(p.month)}${withYear ? ' ' + p.year : ''}`;
}
/** Datum utan veckodag: "12 oktober" / "12 October". */
export function formatDayMonth(d: ISODate, withYear = false): string {
  const p = Temporal.PlainDate.from(d);
  return `${p.day} ${monthName(p.month)}${withYear ? ' ' + p.year : ''}`;
}
/** sv: "12 okt" · en: "12 Oct" */
export function formatDateShort(d: ISODate): string {
  if (currentLang() !== 'en') return formatDateShortSv(d);
  const p = Temporal.PlainDate.from(d);
  return `${p.day} ${monthShort(p.month)}`;
}

/** Klockslag, 24 h: "09.00" (sv) / "09:00" (en). */
export function formatClock(hour: number, minute: number): string {
  return currentLang() === 'en' ? `${pad(hour)}:${pad(minute)}` : `${pad(hour)}.${pad(minute)}`;
}
export function formatTime(i: Temporal.Instant, tz: string): string {
  const z = i.toZonedDateTimeISO(tz);
  return formatClock(z.hour, z.minute);
}
/** "2026-10-11 09.00" / "2026-10-11 09:00" i visningszonen. */
export function formatInstant(iso: ISOInstant, tz: string): string {
  const z = Temporal.Instant.from(iso).toZonedDateTimeISO(tz);
  return `${z.toPlainDate().toString()} ${formatClock(z.hour, z.minute)}`;
}

/** Veckodag och datum: "tis 16 juni" / "Tue 16 June" (long: "tisdag 16 juni" / "Tuesday 16 June"). */
export function formatWeekdayDate(d: ISODate, long = false): string {
  return `${weekdayName(Temporal.PlainDate.from(d).dayOfWeek, !long)} ${formatDayMonth(d)}`;
}
/** Klockslaget i en lokal väggtid ("2026-06-16T08:00" → "08.00" / "08:00"). Zonen ändras aldrig. */
export function formatLocalClock(local: string): string {
  return formatClock(Number(local.slice(11, 13)), Number(local.slice(14, 16)));
}
/** Längd i minuter: "45 min", "1 h", "1 h 30 min" (samma på båda språken). */
export function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}
/** Hur länge sedan ett datum var, räknat i hela dagar: "idag", "igår", "för 3 dagar sedan". */
export function formatDaysAgo(date: ISODate, today: ISODate): string {
  const days = Temporal.PlainDate.from(date).until(Temporal.PlainDate.from(today)).days;
  if (days <= 0) return tr('idag', 'today');
  if (days === 1) return tr('igår', 'yesterday');
  return tr(`för ${days} dagar sedan`, `${days} days ago`);
}

// ---------------------------------------------------------------------------
// Deadlinestatus, taggar, ikoner, standardnamn
// ---------------------------------------------------------------------------

/** Etikett för deadlineStatus (core) på valt språk. Samma betydelse: AutoDay vet bara vad som markerats här. */
export function deadlineLabel(st: DeadlineStatus): string {
  switch (st.urgency) {
    case 'done':
      return tr('Klar här (lokal kvittering)', 'Done here (local check-off)');
    case 'passed':
      return tr('Datum passerat, inte markerad klar här', 'Date passed, not marked done here');
    case 'today':
      return tr('Idag', 'Today');
    default:
      if (st.daysLeft === 1) return tr('Imorgon', 'Tomorrow');
      return tr(`${st.daysLeft} dagar kvar`, `${st.daysLeft} days left`);
  }
}

const TAG_EN: Record<KnownTag, string> = {
  '[MÅSTE]': 'The import source states that this is an obligation. AutoDay has not verified the rule.',
  '[FÖRBEREDELSE]': 'The import source states a preparation step for an obligation. It is not optional.',
  '[FRIVILLIG KONTROLL]': 'The import source states an optional check. Read the original text for what applies.',
  '[KONTROLLERA]': 'The import source asks you to check the item against the original.',
  '[INGEN BETALNING]': 'The import source states that this item is not a payment. It does not mean that returns or other payments can be skipped – read the original text.',
  '[TEST]': 'The import source marks the item as a test.',
};
export function tagExplanation(info: TagInfo): string {
  if (currentLang() !== 'en') return tagExplanationSv(info);
  if (info.tag) return TAG_EN[info.tag];
  if (info.rawPrefix) return `Unknown tag ${info.rawPrefix} from the import source. AutoDay does not interpret it.`;
  return 'No tag in the source.';
}

const ICON_EN: Record<IconId, string> = {
  home: 'Home',
  briefcase: 'Briefcase',
  hospital: 'Hospital',
  stethoscope: 'Doctor',
  building: 'Building',
  landmark: 'Authority',
  receipt: 'Receipt',
  wallet: 'Wallet',
  dumbbell: 'Dumbbell',
  plane: 'Flight',
  car: 'Car',
  calendar: 'Calendar',
  star: 'Star',
  book: 'Book',
  users: 'People',
  utensils: 'Food',
  pill: 'Medicine',
  baby: 'Child',
  graduation: 'Education',
  music: 'Music',
  heart: 'Heart',
};
export function iconLabel(icon: IconId): string {
  return currentLang() === 'en' ? ICON_EN[icon] ?? icon : ICON_LABELS[icon] ?? icon;
}

/**
 * Systemgenererade standardnamn översätts bara så länge de är orörda. Ett namn som användaren ändrat
 * (eller som kommer från en import) visas exakt som det är – inget skrivs om i datan.
 */
const CATEGORY_EN: Record<string, string> = { privat: 'Personal', arbete: 'Work', halsa: 'Health', myndighet: 'Authority', ekonomi: 'Finance', traning: 'Exercise', resa: 'Travel', annat: 'Other' };
const BUILTIN_CATEGORY_SV = Object.fromEntries(BUILTIN_CATEGORIES.map((c) => [c.id, c.name]));
export function categoryName(cat: Pick<Category, 'id' | 'name'> | undefined, fallbackId?: string): string {
  const id = cat?.id ?? fallbackId ?? 'annat';
  const name = cat?.name ?? BUILTIN_CATEGORY_SV[id] ?? '';
  if (currentLang() === 'en' && BUILTIN_CATEGORY_SV[id] === name && CATEGORY_EN[id]) return CATEGORY_EN[id]!;
  return name;
}

const DEFAULT_CALENDAR_SV: Record<string, string> = Object.fromEntries([...DEFAULT_CALENDARS, SKATTEFIX_CALENDAR].map((c) => [c.id, c.name]));
const CALENDAR_EN: Record<string, string> = { 'cal-privat': 'Personal', 'cal-arbete': 'Work', 'cal-skattefix': 'SkatteFix (imported)' };
export function calendarName(cal: Pick<CalendarDef, 'id' | 'name'> | undefined, fallbackName = ''): string {
  if (!cal) return fallbackName;
  if (currentLang() === 'en' && DEFAULT_CALENDAR_SV[cal.id] === cal.name && CALENDAR_EN[cal.id]) return CALENDAR_EN[cal.id]!;
  // Importkalendrar får namnet "<källa> (importerad)" av appen; källans namn lämnas orört.
  if (currentLang() === 'en' && cal.name.endsWith(' (importerad)')) return cal.name.slice(0, -' (importerad)'.length) + ' (imported)';
  return cal.name;
}

// ---------------------------------------------------------------------------
// Felmeddelanden från domänen och servern
// ---------------------------------------------------------------------------

/**
 * Domänen (core) och servern skriver felmeddelanden på svenska; de är också API-svar. I engelskt läge
 * översätts kända meddelanden här. Okända visas oförändrade hellre än att något hittas på.
 */
const ERRORS_EN: Array<[RegExp, string | ((m: RegExpExecArray) => string)]> = [
  [/^Titel saknas\.$/, 'Title is missing.'],
  [/^Titeln är för lång\.$/, 'The title is too long.'],
  [/^Okänd kalender\.$/, 'Unknown calendar.'],
  [/^Okänd kategori\.$/, 'Unknown category.'],
  [/^Importerade kalendrar är skrivskyddade\. Välj Privat eller Arbete\.$/, 'Imported calendars are read-only. Choose Personal or Work.'],
  [/^Slutdatum måste vara efter startdatum\.$/, 'The end date must be after the start date.'],
  [/^Sluttiden är före starttiden\.$/, 'The end time is before the start time.'],
  [/^Högst 10 påminnelser per aktivitet\.$/, 'At most 10 reminders per activity.'],
  [/^Länken måste börja med https:\/\/ eller http:\/\/\.$/, 'The link must start with https:// or http://.'],
  [/^Förekomsten finns inte\.$/, 'The occurrence does not exist.'],
  [/^Ange förekomst\.$/, 'Choose an occurrence.'],
  [/^Inget att granska\.$/, 'Nothing to review.'],
  [/^Bekräfta att du har kontrollerat att åtgärden är utförd\.$/, 'Confirm that you have checked that the action is done.'],
  [/^För många kopior på en gång \(max 500\)\.$/, 'Too many copies at once (max 500).'],
  [/^Inga egna aktiviteter att spara som mall\.$/, 'No activities of your own to save as a template.'],
  [/^Aktiviteten hittades inte\.$/, 'The activity was not found.'],
  [/^Den importerade posten hittades inte\.$/, 'The imported item was not found.'],
  [/^Källdatum från SkatteFix\. Planera arbetet separat\.$/, 'Source date from SkatteFix. Plan the work separately.'],
  [/^Aktiviteten har ändrats efter åtgärden och kan inte ångras automatiskt\.$/, 'The activity has changed since the action and cannot be undone automatically.'],
  [/^Okänd frekvens\.$/, 'Unknown frequency.'],
  [/^Intervallet måste vara 1–99\.$/, 'The interval must be 1–99.'],
  [/^Antal förekomster måste vara 1–1000\.$/, 'The number of occurrences must be 1–1000.'],
  [/^Ogiltig veckodag\.$/, 'Invalid weekday.'],
  [/^Klockslaget (\S+ \S+) finns inte i (\S+) \(sommartidsomställning\)\.( Välj en annan tid\.)?$/, (m) => `The time ${m[1]} does not exist in ${m[2]} (daylight saving change).${m[3] ? ' Choose another time.' : ''}`],
  [/^Okänd importkälla\.$/, 'Unknown import source.'],
  [/^Endast den senaste importen kan ångras\. Ångra nyare importer först\.$/, 'Only the latest import can be undone. Undo newer imports first.'],
  [/^Filen är (\d+) kB\. Gränsen är (\d+) kB\.$/, (m) => `The file is ${m[1]} kB. The limit is ${m[2]} kB.`],
  [/^Filen innehåller (\d+) händelser\. Gränsen är (\d+)\.$/, (m) => `The file contains ${m[1]} events. The limit is ${m[2]}.`],
  [/^Filen kunde inte tolkas som iCalendar: (.*)$/, (m) => `The file could not be read as iCalendar: ${m[1]}`],
  [/^Filen innehåller ingen VCALENDAR\.$/, 'The file contains no VCALENDAR.'],
  [/^Filen innehåller fler än (\d+) larm\.$/, (m) => `The file contains more than ${m[1]} alarms.`],
  [/^Ingen migrering från schemaversion (\S+)\.$/, (m) => `No migration from schema version ${m[1]}.`],
  [/^Datan kommer från en nyare AutoDay \(schema (\S+)\)\. Uppdatera appen först\.$/, (m) => `The data comes from a newer AutoDay (schema ${m[1]}). Update the app first.`],
  [/^Säkerhetskopian är för stor\.$/, 'The backup is too large.'],
  [/^Filen är inte giltig JSON\.$/, 'The file is not valid JSON.'],
  [/^Filen är ingen AutoDay-säkerhetskopia\.$/, 'The file is not an AutoDay backup.'],
  [/^Okänt säkerhetskopieformat\.$/, 'Unknown backup format.'],
  [/^Säkerhetskopian saknar data\.$/, 'The backup contains no data.'],
  [/^Kontrollsumman stämmer inte – filen är skadad eller ändrad\.$/, 'The checksum does not match – the file is damaged or modified.'],
  [/^Max (\d+) snoozningar per påminnelse\.$/, (m) => `At most ${m[1]} snoozes per reminder.`],
  [/^Tiden är inte längre ledig\. Sök igen\.$/, 'The time is no longer free. Search again.'],
  // Förberedelseplaner (mallar och regler; godkännanden har egna, översatta besked i prep/commit.ts).
  [/^Mallen hittades inte\.$/, 'The template was not found.'],
  [/^Regeln hittades inte\.$/, 'The rule was not found.'],
  [/^Planen hittades inte\.$/, 'The plan was not found.'],
  [/^Mallen har ändrats på en annan enhet eller i en annan flik\. Öppna den igen\.$/, 'The template was changed on another device or in another tab. Open it again.'],
  [/^Mallen för regeln finns inte längre\. Välj en annan mall först\.$/, 'The rule’s template no longer exists. Choose another template first.'],
  [/^Förslaget är inte avfärdat\.$/, 'The suggestion is not dismissed.'],
  [/^Förslaget kan inte godkännas\.$/, 'The suggestion cannot be approved.'],
  [/^Förslaget är inte längre aktuellt\. Sök igen\.$/, 'The suggestion is no longer up to date. Search again.'],
  [/^Importkällan hittades inte\.$/, 'The import source was not found.'],
  [/^Inget att ångra\.$/, 'Nothing to undo.'],
  [/^Datan har ändrats i en annan flik – läses om\.$/, 'The data was changed in another tab – reloading.'],
  [/^Ändringen kunde inte sparas på enheten och har INTE genomförts\. ?(.*)$/, (m) => `The change could not be saved on the device and has NOT been made. ${m[1] ?? ''}`.trim()],
  // Importens varningar och bekräftelser (förhandsgranskningen).
  [/^(.*) var längre än (\d+) tecken och kortades i visningen\.$/, (m) => `${m[1]} was longer than ${m[2]} characters and was shortened for display.`],
  [/^Filen var inte giltig UTF-8 och tolkades som Windows-1252\. Kontrollera å, ä och ö\.$/, 'The file was not valid UTF-8 and was read as Windows-1252. Check å, ä and ö.'],
  [/^Filen är en mötesinbjudan\/svar \(METHOD:(.*)\)\. AutoDay importerar bara datumen och skickar aldrig svar eller mejl\.$/, (m) => `The file is a meeting invitation/reply (METHOD:${m[1]}). AutoDay only imports the dates and never sends replies or email.`],
  [/^(\d+) st (\S+) stöds inte och importerades inte\.$/, (m) => `${m[1]} × ${m[2]} are not supported and were not imported.`],
  [/^En händelse \("(.*)"\) saknar UID och kan inte identifieras stabilt\. Den importerades inte\.$/, (m) => `An event ("${m[1]}") has no UID and cannot be identified reliably. It was not imported.`],
  [/^Händelsen (.*) saknar giltig DTSTART och importerades inte\.$/, (m) => `The event ${m[1]} has no valid DTSTART and was not imported.`],
  [/^UID (.*) förekommer flera gånger i filen\. Den sista förekomsten används\.$/, (m) => `UID ${m[1]} occurs several times in the file. The last occurrence is used.`],
  [/^Ogiltigt datum i (.*)\.$/, (m) => `Invalid date in ${m[1]}.`],
  [/^RDATE i (.*) stöds inte; extra datum visas inte\.$/, (m) => `RDATE in ${m[1]} is not supported; extra dates are not shown.`],
  [/^Bilagor \(ATTACH\) i "(.*)" hämtas aldrig och visas inte\.$/, (m) => `Attachments (ATTACH) in "${m[1]}" are never fetched and are not shown.`],
  [/^Deltagare\/organisatör i "(.*)" sparas inte\. Inga svar skickas\.$/, (m) => `Attendees/organiser in "${m[1]}" are not saved. No replies are sent.`],
  [/^"(.*)" har (\d+) larm; endast de (\d+) första används\.$/, (m) => `"${m[1]}" has ${m[2]} alarms; only the first ${m[3]} are used.`],
  [/^Larm med ACTION:(.*) i "(.*)" sparas men utlöser aldrig mejl eller program\. Det visas inte som notis\.$/, (m) => `An alarm with ACTION:${m[1]} in "${m[2]}" is saved but never triggers email or programs. It is not shown as a notification.`],
  [/^Ljudlarm i "(.*)" visas som vanlig notis; ljudbilagor hämtas inte\.$/, (m) => `Sound alarm in "${m[1]}" is shown as a normal notification; sound attachments are not fetched.`],
  [/^(\d+) tid\(er\) använder okänd tidszon "(.*)"\. Välj vilken zon den avser innan import\.$/, (m) => `${m[1]} time(s) use the unknown time zone "${m[2]}". Choose which zone it means before importing.`],
  [/^(\d+) tid\(er\) saknar tidszon\. Välj vilken zon de avser innan import – AutoDay antar inte UTC\.$/, (m) => `${m[1]} time(s) have no time zone. Choose which zone they mean before importing – AutoDay does not assume UTC.`],
  [/^Återkomstfrekvens (\S+) stöds inte; endast första förekomsten visas\.$/, (m) => `Recurrence frequency ${m[1]} is not supported; only the first occurrence is shown.`],
  [/^Återkomstregeln använder (.*) som AutoDay inte stöder ännu; endast första förekomsten visas\.$/, (m) => `The recurrence rule uses ${m[1]}, which AutoDay does not support yet; only the first occurrence is shown.`],
  [/^BYDAY=(.*) stöds inte; endast första förekomsten visas\.$/, (m) => `BYDAY=${m[1]} is not supported; only the first occurrence is shown.`],
  [/^Serien har (\d+) förekomster; AutoDay visar högst (\d+)\.$/, (m) => `The series has ${m[1]} occurrences; AutoDay shows at most ${m[2]}.`],
  [/^(\d+) larm har redan passerat\. De sparas som källdata men schemaläggs inte i efterhand\.$/, (m) => `${m[1]} alarms have already passed. They are saved as source data but are not scheduled afterwards.`],
  [/^Filen innehåller (\d+) larm\. Bekräfta att du vill aktivera dem\.$/, (m) => `The file contains ${m[1]} alarms. Confirm that you want to turn them on.`],
  [/^Filen är äldre än den senast importerade exporten\. Bekräfta uttryckligen\.$/, 'The file is older than the latest imported export. Confirm explicitly.'],
  [/^Filen innehåller ovanligt många larm\. Bekräfta uttryckligen\.$/, 'The file contains an unusually large number of alarms. Confirm explicitly.'],
  [/^Tidszonsbeslut saknas\.$/, 'Time zone decisions are missing.'],
  // Ångra-etiketter (operationens namn, visas i "Ångrat: …").
  [/^Granskning klar$/, 'Review done'],
  [/^Ändra anteckning$/, 'Edit note'],
  [/^Flytta "(.*)" \(en förekomst\)$/, (m) => `Move "${m[1]}" (one occurrence)`],
  [/^Flytta "(.*)"$/, (m) => `Move "${m[1]}"`],
  [/^Skapa "(.*)"$/, (m) => `Create "${m[1]}"`],
  [/^Spara mall "(.*)"$/, (m) => `Save template "${m[1]}"`],
  [/^Ta bort en förekomst av "(.*)"$/, (m) => `Delete an occurrence of "${m[1]}"`],
  [/^Ta bort "(.*)"$/, (m) => `Delete "${m[1]}"`],
  [/^Ändra en förekomst av "(.*)"$/, (m) => `Edit an occurrence of "${m[1]}"`],
  [/^Ändra "(.*)"$/, (m) => `Edit "${m[1]}"`],
  [/^Klar här: "(.*)"$/, (m) => `Done here: "${m[1]}"`],
  [/^Återöppnad: "(.*)"$/, (m) => `Reopened: "${m[1]}"`],
  [/^Klar: "(.*)"$/, (m) => `Done: "${m[1]}"`],
  [/^Återöppna "(.*)"$/, (m) => `Reopen "${m[1]}"`],
  [/^Kopiera vecka \((\d+) st\)$/, (m) => `Copy week (${m[1]})`],
  [/^Kopiera dag \((\d+) st\)$/, (m) => `Copy day (${m[1]})`],
  [/^Kopiera mall \((\d+) st\)$/, (m) => `Apply template (${m[1]})`],
  [/^Räknas som deadline: "(.*)"$/, (m) => `Counts as a deadline: "${m[1]}"`],
  [/^Inte en deadline: "(.*)"$/, (m) => `Not a deadline: "${m[1]}"`],
  [/^Källans standard: "(.*)"$/, (m) => `Source default: "${m[1]}"`],
];
export function localizeError(message: string): string {
  if (currentLang() !== 'en') return message;
  for (const [re, en] of ERRORS_EN) {
    const m = re.exec(message);
    if (m) return typeof en === 'string' ? en : en(m);
  }
  return message;
}
